import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { navigate } from "@reach/router";
import LoadingSpinner from "../components/LoadingSpinner";
import LabelModal from "../components/LabelModal";
import { AuthConsumer, AuthContext } from "../helpers/AuthContext";
import Sidebar from "../components/Sidebar";
import {
  BrainIcon,
  MuscleIcon,
  EyeIcon,
  HeartIcon,
  LineNoiseIcon,
  ChanNoiseIcon,
  OtherIcon,
  UnsureIcon,
} from "../components/ClassifierIcons";
import { ImageInstance } from "../utils/ImageInstance";
import "../styles/responsive.css";

const PRACTICE_TARGET = 10;

const PracticePage = () => {
  const ctx = useContext(AuthContext);

  // Image state. Practice doesn't prefetch or hash, so the instance is
  // simpler than LabelPage's — but keeping the same shape avoids a
  // second mental model. `key={currentInstance.id}` on the <img> below
  // means React mounts a fresh DOM node per practice IC, eliminating
  // any cross-IC state contamination at the DOM level.
  const [isLoading, setIsLoading] = useState(false);
  const [currentInstance, setCurrentInstance] = useState(null);

  // Label toggles (keyed like LabelPage)
  const [brain, setBrain] = useState(false);
  const [muscle, setMuscle] = useState(false);
  const [eye, setEye] = useState(false);
  const [heart, setHeart] = useState(false);
  const [linenoise, setLinenoise] = useState(false);
  const [channoise, setChannoise] = useState(false);
  const [other, setOther] = useState(false);
  const [unsure, setUnsure] = useState(false);

  // Submission + UI state
  const [prac, setPrac] = useState(null); // tags of the current practice submission
  const [status, setStatus] = useState(""); // "" | "success"
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState("");

  // Practice gate state (server-authoritative after mount + after each submit)
  const [practiceCount, setPracticeCount] = useState(0);
  const [practiceCorrect, setPracticeCorrect] = useState(0);
  const [practicePassed, setPracticePassed] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [submittingPractice, setSubmittingPractice] = useState(false);
  const [resetting, setResetting] = useState(false);

  const isLoadingRef = useRef(false);

  // ==================================================================
  // Mount: fetch authoritative practice state from the server
  // ==================================================================
  useEffect(() => {
    if (ctx.Auth === "Guest" || ctx.Auth == null) {
      // AuthProvider hasn't hydrated from localStorage yet; wait for the
      // next run when Auth flips to User/Admin. Without this guard, the
      // effect would falsely treat every fresh-loaded user as a non-User
      // and latch practicePassed = true, hiding the gate banner forever.
      return;
    }
    if (ctx.Auth !== "User") {
      setPracticePassed(true); // admins aren't gated
      return;
    }
    axios
      .get("/api/users/practice-status", {
        headers: ctx.authHeader ? ctx.authHeader() : {},
      })
      .then((res) => {
        const d = res.data || {};
        setPracticeCount(d.practiceCount || 0);
        setPracticeCorrect(d.practiceCorrect || 0);
        setPracticePassed(!!d.practicePassed);
        if (d.practicePassed && ctx.setPracticePassed) ctx.setPracticePassed(true);
      })
      .catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          // Session expired — bounce to login
          navigate("/login");
        }
      });
  }, [ctx.Auth]);

  // ==================================================================
  // Image loading
  // ==================================================================
  const getImage = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    setError("");
    setValidationError("");

    setBrain(false);
    setMuscle(false);
    setEye(false);
    setHeart(false);
    setLinenoise(false);
    setChannoise(false);
    setOther(false);
    setUnsure(false);

    try {
      const filenameResponse = await axios.get("/dropbox/practicefile", {
        timeout: 30000,
      });
      if (!filenameResponse.data) throw new Error("No filename received");
      const filename = filenameResponse.data;

      const imageResponse = await axios.get("/dropbox/imagedata", {
        responseType: "arraybuffer",
        params: { imagefile: filename },
        timeout: 30000,
      });
      if (!imageResponse.data || imageResponse.data.byteLength === 0) {
        throw new Error("Empty image data received");
      }
      const base64 = btoa(
        new Uint8Array(imageResponse.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      // Build the instance from base64 so (filename, src) become atomic.
      // Default mime is "image/png" but the original component used a
      // jpeg charset prefix when rendering — pass jpeg here to match.
      setCurrentInstance(
        ImageInstance.fromBase64(filename, base64, "image/jpeg;charset=utf-8")
      );
    } catch (err) {
      console.error("Error loading image:", err);
      let msg = "Error loading image. ";
      if (err.code === "ECONNABORTED") msg += "Request timed out.";
      else if (err.response)
        msg += err.response.data?.error || err.response.statusText || "Server error.";
      else if (err.request) msg += "No response from server.";
      else msg += err.message || "Unknown error.";
      setError(msg);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  const getNext = () => {
    setStatus("");
    setError("");
    setValidationError("");
    setCurrentInstance(null);
    setPrac(null);
    setLastResult(null);
    getImage();
  };

  useEffect(() => {
    if (!status && !currentInstance && !isLoading) {
      getImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================================================================
  // Submit / reset / clear handlers
  // ==================================================================
  const clearAllSelections = () => {
    setBrain(false);
    setMuscle(false);
    setEye(false);
    setHeart(false);
    setLinenoise(false);
    setChannoise(false);
    setOther(false);
    setUnsure(false);
    setValidationError("");
  };

  const validateSelection = () => {
    const any = brain || muscle || eye || heart || linenoise || channoise || other || unsure;
    if (!any) {
      setValidationError(
        "Please select at least one label before submitting to see results."
      );
      return false;
    }
    setValidationError("");
    return true;
  };

  const submitResults = () => {
    if (!validateSelection()) return;
    const tags = [];
    if (brain) tags.push("Brain");
    if (muscle) tags.push("Muscle");
    if (eye) tags.push("Eye");
    if (heart) tags.push("Heart");
    if (linenoise) tags.push("Line Noise");
    if (channoise) tags.push("Channel Noise");
    if (other) tags.push("Other");
    if (unsure) tags.push("Unsure");

    setPrac(tags);
    setStatus("success");

    const isUserGated = ctx.Auth === "User" && !practicePassed;
    if (!isUserGated) {
      setLastResult(null);
      return;
    }

    setSubmittingPractice(true);
    // Snapshot the filename at submit click — currentInstance can change
    // (Skip / next IC) while the request is in flight, and we don't
    // want the practice attempt to be saved against a different IC.
    const submittedFilename = currentInstance?.filename || "";
    axios
      .post(
        "/api/users/practice-submit",
        { componentName: submittedFilename, tags },
        { headers: ctx.authHeader ? ctx.authHeader() : {} }
      )
      .then((res) => {
        const d = res.data || {};
        setPracticeCount(d.practiceCount || 0);
        setPracticeCorrect(d.practiceCorrect || 0);
        setPracticePassed(!!d.practicePassed);
        setLastResult({
          correct: d.correct,
          consensusTags: d.consensusTags || [],
          ungradeable: !!d.ungradeable,
        });
        if (d.practicePassed && ctx.setPracticePassed) {
          ctx.setPracticePassed(true);
        }
      })
      .catch((err) => {
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          setError(
            "Your session expired. Please sign in again to continue practice."
          );
          setTimeout(() => navigate("/login"), 1200);
          return;
        }
        setError(
          "Couldn't save your practice attempt. Please refresh or sign in again."
        );
        setLastResult({ correct: null, consensusTags: [], error: true });
      })
      .finally(() => setSubmittingPractice(false));
  };

  const handleReset = () => {
    if (!window.confirm("Reset practice progress and try again?")) return;
    setResetting(true);
    axios
      .post(
        "/api/users/practice-reset",
        {},
        { headers: ctx.authHeader ? ctx.authHeader() : {} }
      )
      .then(() => {
        setPracticeCount(0);
        setPracticeCorrect(0);
        setPracticePassed(false);
        setLastResult(null);
        getNext();
      })
      .catch((err) => {
        alert(err?.response?.data?.message || "Reset failed");
      })
      .finally(() => setResetting(false));
  };

  // ==================================================================
  // Label options — IDENTICAL to LabelPage so practice matches real UI
  // ==================================================================
  const labelOptions = [
    { key: "brain", label: "Brain", icon: <BrainIcon />, shortcut: "1", color: "#6366F1", state: brain, setter: setBrain },
    { key: "muscle", label: "Muscle", icon: <MuscleIcon />, shortcut: "2", color: "#EF4444", state: muscle, setter: setMuscle },
    { key: "eye", label: "Eye", icon: <EyeIcon />, shortcut: "3", color: "#3B82F6", state: eye, setter: setEye },
    { key: "heart", label: "Heart", icon: <HeartIcon />, shortcut: "4", color: "#EC4899", state: heart, setter: setHeart },
    { key: "linenoise", label: "Line Noise", icon: <LineNoiseIcon />, shortcut: "6", color: "#10B981", state: linenoise, setter: setLinenoise },
    { key: "channoise", label: "Chan Noise", icon: <ChanNoiseIcon />, shortcut: "7", color: "#06B6D4", state: channoise, setter: setChannoise },
    { key: "other", label: "Other", icon: <OtherIcon />, shortcut: "8", color: "#F59E0B", state: other, setter: setOther },
    { key: "unsure", label: "Unsure", icon: <UnsureIcon />, shortcut: "9", color: "#6B7280", state: unsure, setter: setUnsure },
  ];

  // ==================================================================
  // Keyboard shortcuts (1-9, C clear, Enter submit/next)
  // ==================================================================
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      const opt = labelOptions.find((o) => o.shortcut === e.key);
      if (opt && !status) {
        opt.setter(!opt.state);
        setValidationError("");
        return;
      }
      if (e.key.toLowerCase() === "c" && !status) {
        clearAllSelections();
        return;
      }
      if (e.key === "Enter") {
        if (status === "success") {
          getNext();
        } else {
          submitResults();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brain, muscle, eye, heart, linenoise, channoise, other, unsure, status]);

  // ==================================================================
  // Render
  // ==================================================================
  return (
    <AuthConsumer>
      {({ Auth, Email }) => {
        const isUser = Auth === "User";
        const showGate = isUser && !practicePassed;
        const accuracyPct =
          practiceCount > 0 ? Math.round((practiceCorrect / practiceCount) * 100) : 0;
        const finished = showGate && practiceCount >= PRACTICE_TARGET;

        return (
          <div>
            <Sidebar />
            <div className="page-content" style={{ paddingTop: 100 }}>
              <div style={{ padding: "0 24px" }}>
                {/* ================= PRACTICE GATE HEADER ================= */}
                {showGate && (
                  <div
                    style={{
                      marginBottom: 18,
                      padding: "18px 22px",
                      borderRadius: 14,
                      border: "1px solid rgba(8, 145, 178, 0.28)",
                      background: "rgba(255,255,255,0.92)",
                      boxShadow: "0 6px 24px rgba(15,23,42,0.06)",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "#0891b2",
                            marginBottom: 4,
                          }}
                        >
                          ● Practice Round — Required
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: "#0a0e1a" }}>
                          Label {PRACTICE_TARGET} ICs to unlock the live queue —
                          your accuracy is reported as feedback only
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div
                            style={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: 22,
                              fontWeight: 700,
                              color: "#0891b2",
                            }}
                          >
                            {practiceCount}/{PRACTICE_TARGET}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                              color: "#6b7280",
                              fontWeight: 600,
                            }}
                          >
                            Submitted
                          </div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div
                            style={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: 22,
                              fontWeight: 700,
                              color: "#0891b2",
                            }}
                          >
                            {practiceCount > 0 ? `${accuracyPct}%` : "—"}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                              color: "#6b7280",
                              fontWeight: 600,
                            }}
                          >
                            Accuracy
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: 12,
                        width: "100%",
                        height: 6,
                        background: "rgba(8, 145, 178, 0.08)",
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(
                            100,
                            (practiceCount / PRACTICE_TARGET) * 100
                          )}%`,
                          height: "100%",
                          background: "#0891b2",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* ================= COMPLETION CARD ================= */}
                {finished && (
                  <div
                    style={{
                      marginBottom: 22,
                      padding: "24px 26px",
                      borderRadius: 14,
                      border: "1px solid rgba(5, 150, 105, 0.4)",
                      background: "rgba(5, 150, 105, 0.06)",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "#059669",
                        marginBottom: 6,
                      }}
                    >
                      ● Practice complete
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#0a0e1a", marginBottom: 6 }}>
                      You got {practiceCorrect} out of {practiceCount} correct
                      ({accuracyPct}%)
                    </div>
                    <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 16 }}>
                      The live labeling queue is now unlocked.
                    </div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <button
                        onClick={() => navigate("/label")}
                        style={{
                          background: "#0891b2",
                          color: "white",
                          border: "1px solid #0891b2",
                          padding: "10px 22px",
                          borderRadius: 8,
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 600,
                          fontSize: 12,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          cursor: "pointer",
                        }}
                      >
                        Go to live labeling →
                      </button>
                    </div>
                  </div>
                )}

                {/* ================= PER-ATTEMPT FEEDBACK ================= */}
                {showGate &&
                  lastResult &&
                  lastResult.correct !== null &&
                  !lastResult.error && (
                    <div
                      style={{
                        marginBottom: 18,
                        padding: "12px 16px",
                        borderRadius: 10,
                        border: lastResult.correct
                          ? "1px solid rgba(5, 150, 105, 0.4)"
                          : "1px solid rgba(220, 38, 38, 0.35)",
                        background: lastResult.correct
                          ? "rgba(5, 150, 105, 0.06)"
                          : "rgba(220, 38, 38, 0.06)",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: lastResult.correct ? "#059669" : "#dc2626",
                        }}
                      >
                        {lastResult.correct
                          ? "✓ Correct — your label matches the consensus"
                          : "✗ Incorrect — that didn't match the consensus"}
                      </div>
                      <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
                        Consensus tags:{" "}
                        <strong>
                          {(lastResult.consensusTags || []).join(", ") || "—"}
                        </strong>
                      </div>
                    </div>
                  )}

                {/* ================= ERROR BANNER ================= */}
                {error && (
                  <div
                    style={{
                      marginBottom: 14,
                      padding: "12px 16px",
                      borderRadius: 8,
                      background: "rgba(220, 38, 38, 0.08)",
                      border: "1px solid rgba(220, 38, 38, 0.3)",
                      color: "#dc2626",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 14,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <span>{error}</span>
                    <button
                      onClick={() => setError("")}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#dc2626",
                        cursor: "pointer",
                        fontSize: 18,
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* ================= VALIDATION BANNER ================= */}
                {validationError && (
                  <div
                    style={{
                      marginBottom: 14,
                      padding: "10px 16px",
                      borderRadius: 8,
                      background: "rgba(217, 119, 6, 0.08)",
                      border: "1px solid rgba(217, 119, 6, 0.3)",
                      color: "#d97706",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 14,
                    }}
                  >
                    {validationError}
                  </div>
                )}

                {/* ================= IMAGE + LABEL-GRID LAYOUT ================= */}
                {isLoading ? (
                  <LoadingSpinner />
                ) : currentInstance ? (
                  <div className="main-content">
                    {/* Image Section — same sizing as live LabelPage */}
                    <div className="image-section">
                      <div className="section-header">
                        <h2>Signal Visualization</h2>
                      </div>
                      <div className="image-container">
                        <div className="zoom-wrapper">
                          {/*
                            key={currentInstance.id} → React mounts a
                            fresh <img> per practice IC. Same structural
                            isolation as LabelPage; no cross-IC DOM-state
                            contamination is possible.
                          */}
                          <img
                            key={currentInstance.id}
                            src={currentInstance.src}
                            alt="Labeling data"
                            className="signal-image"
                            style={{ opacity: status ? 0.33 : 1.0 }}
                            onError={() => {
                              setError(
                                "Failed to display image. The image may be corrupted."
                              );
                              setCurrentInstance(null);
                            }}
                          />
                        </div>
                      </div>

                      {/* Scroll trace (time series) — horizontally scrollable,
                          same source as the live labeling page. Practice was
                          missing this; labelers need it to judge some ICs. */}
                      <div
                        className="practice-scroll-wrap"
                        style={{ marginTop: 14 }}
                      >
                        <div className="section-header">
                          <h2>Scroll trace — scroll right →</h2>
                        </div>
                        <div
                          style={{
                            overflowX: "auto",
                            border: "1px solid #d7dee5",
                            borderRadius: 8,
                            background: "#fff",
                            WebkitOverflowScrolling: "touch",
                          }}
                        >
                          <img
                            key={"scroll-" + currentInstance.id}
                            src={
                              "/dropbox/scrolldata?imagefile=" +
                              encodeURIComponent(currentInstance.filename)
                            }
                            alt="Scroll trace"
                            style={{
                              height: 300,
                              maxWidth: "none",
                              display: "block",
                            }}
                            onLoad={(e) => {
                              const w = e.currentTarget.closest(
                                ".practice-scroll-wrap"
                              );
                              if (w) w.style.display = "";
                            }}
                            onError={(e) => {
                              const w = e.currentTarget.closest(
                                ".practice-scroll-wrap"
                              );
                              if (w) w.style.display = "none";
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Control Section — same sizing as live LabelPage */}
                    <div className="control-section">
                      <div className="section-header">
                        <h2>Classification Labels</h2>
                        <button
                          className="clear-button"
                          onClick={clearAllSelections}
                          disabled={!!status}
                          title="Clear All (C)"
                        >
                          Clear
                        </button>
                      </div>

                      <p
                        style={{
                          margin: "0 0 12px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#4b5563",
                        }}
                      >
                        Select every class that applies — an IC is not limited to
                        one label. Check all that fit (e.g. brain + eye).
                      </p>

                      <div className="labels-grid">
                        {labelOptions.map(
                          ({ key, label, icon, shortcut, color, state, setter }) => (
                            <button
                              key={key}
                              className={`label-button ${state ? "active" : ""}`}
                              onClick={() => {
                                setter(!state);
                                setValidationError("");
                              }}
                              disabled={!!status || submittingPractice}
                              data-shortcut={shortcut}
                              style={{
                                "--btn-color": color,
                                "--btn-color-light": `${color}22`,
                                background: state ? color : `${color}22`,
                                borderColor: state ? color : "#e5e7eb",
                                color: state ? "#fff" : "#4b5563",
                              }}
                            >
                              <span className="label-icon">{icon}</span>
                              <span className="label-text">{label}</span>
                              <kbd className="label-key">{shortcut}</kbd>
                              {state && <span className="label-check">✓</span>}
                            </button>
                          )
                        )}
                      </div>

                      <div className="action-buttons">
                        {!status ? (
                          <button
                            className="btn-primary"
                            onClick={submitResults}
                            disabled={submittingPractice}
                            title="Submit (Enter)"
                          >
                            {submittingPractice ? "Submitting…" : "Submit Labels"}
                          </button>
                        ) : (
                          <button
                            className="btn-success"
                            onClick={getNext}
                            title="Next (Enter)"
                          >
                            Next Image →
                          </button>
                        )}

                        {!status && (
                          <button
                            className="btn-secondary skip-btn"
                            onClick={getNext}
                            disabled={submittingPractice}
                            title="Skip (S)"
                          >
                            Skip Image
                          </button>
                        )}
                      </div>

                      {/* Inline success: show results + labels submitted */}
                      {status === "success" && (
                        <div
                          style={{
                            marginTop: 14,
                            padding: "12px 16px",
                            borderRadius: 8,
                            background: "rgba(5, 150, 105, 0.06)",
                            border: "1px solid rgba(5, 150, 105, 0.3)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: 13,
                              color: "#059669",
                              fontWeight: 600,
                            }}
                          >
                            ✓ Labels submitted
                          </span>
                          <LabelModal
                            open={false}
                            getStatus={true}
                            compName={currentInstance?.filename || ""}
                            compData={null}
                            pracData={prac}
                            currentUserEmail={Email}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
                    <p>No image to display.</p>
                    <button className="btn-primary" onClick={getNext}>
                      Load Image
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }}
    </AuthConsumer>
  );
};

export default PracticePage;
